export enum AnnouncementAudience {
  Workers = 1,
  Clients = 2,
  SubcontractorEmployees = 4,
  OfficeStaff = 8,
  All = 7   // Workers | Clients | SubcontractorEmployees
}

export enum AnnouncementChannel {
  FrontPage = 1,
  Email = 2,
  SMS = 4
}

/** How loud an announcement is. Independent of acknowledgment. */
export enum AnnouncementPriority {
  Low = 1,     // inbox + bell count only
  Normal = 2,  // + dismissable arrival banner
  High = 3     // + blocking dialog at the next navigation
}

/** Codes the scheduler writes to `scheduleFailureReason`; translated as ANNOUNCEMENTS.SCHEDULE.FAILURE.<code>. */
export type AnnouncementScheduleFailure = 'SCOPE_LOST' | 'HIGH_PRIORITY_NOT_ALLOWED' | 'NO_WRITE_RIGHT' | 'NO_RECIPIENTS' | 'TARGET_LOST';

export interface AnnouncementAttachment {
  id: number;
  fileName: string;
  downloadUrl: string;   // relative API path, e.g. /api/announcements/attachments/3/download
  contentType: string;
  fileSize: number;      // bytes
  uploadedAt: string;    // ISO 8601
}

export interface Announcement {
  id: number;
  title: string;
  body: string;
  audience: AnnouncementAudience;
  channels: AnnouncementChannel;
  projectIds: number[];
  /**
   * Names of the projects in `projectIds`, delivered by GET /{id} so a reader sees names
   * even for projects they cannot select themselves. Absent on older API builds and on
   * list responses - then ids are resolved against the reader's own project list.
   */
  projects?: { id: number; name: string }[] | null;
  createdAt: string;
  createdById: string;
  createdByName: string;
  sentAt?: string;
  isDraft: boolean;
  isActive: boolean;
  priority: AnnouncementPriority;
  requiresAcknowledgment: boolean;
  /** When the announcement became visible to recipients (UTC). Null on drafts. */
  publishedAt?: string | null;
  /** Set only on drafts: the worker publishes the draft at this time (UTC). */
  scheduledPublishAt?: string | null;
  /** Set when a due schedule was refused; the announcement stays a draft. */
  scheduleFailureReason?: AnnouncementScheduleFailure | null;
  /** 1 until the first edit after publishing; each edit of a published announcement adds 1. */
  version: number;
  /** Last edit of the published announcement (UTC), null if never edited. Drives the "Muokattu" marker. */
  editedAt?: string | null;
  editedByName?: string | null;
  recipientCount: number;
  seenCount: number;
  /** Withheld (0) from callers who may not see stats, exactly like seenCount. */
  acknowledgedCount?: number | null;
  /** Present only for the draft author or an announcement manager. */
  excludeInactive?: boolean;
  excludeNeverLoggedIn?: boolean;
  excludeWorkersWithoutRecentHourEntriesMonths?: number;
  excludedUserIds?: string[];
  targetRoleIds?: string[];
  targetGroupIds?: number[];
  includedUserIds?: string[];
  /** Populated on GET /my and GET /{id} when the caller is a recipient; null otherwise. */
  seen: boolean | null;
  /** Timestamp when the caller first marked this announcement as seen. Populated alongside `seen`. */
  seenAt?: string;
  /** When the caller acknowledged it. Populated alongside `seen`. */
  acknowledgedAt?: string | null;
  /** Always present on new records; may be null/undefined from older API responses. */
  attachments: AnnouncementAttachment[] | null | undefined;
}

export interface AnnouncementRecipient {
  id: number;
  announcementId: number;
  userId: string;
  userName: string;
  userEmail?: string;
  userPhone?: string;
  employerName?: string;
  lastHourEntryAt?: Date;
  emailSent: boolean;
  emailSentAt?: Date;
  emailError?: string;
  smsSent: boolean;
  smsSentAt?: Date;
  smsError?: string;
  seen: boolean;
  seenAt?: Date;
  acknowledgedAt?: Date | null;
  /** Announcement version the recipient acknowledged; older than `Announcement.version` = acknowledged an earlier text. */
  acknowledgedVersion?: number | null;
}

export interface RecipientEmployerFacet {
  /** Null = recipients with no employer company. */
  name: string | null;
  count: number;
}

export interface AnnouncementRecipientPage {
  items: AnnouncementRecipient[];
  totalCount: number;
  page: number;
  pageSize: number;
  /** Employer counts over the whole announcement (not the filtered page). */
  employers: RecipientEmployerFacet[];
}

export type RecipientSortField =
  'name' | 'employer' | 'email' | 'lastHourEntry' | 'emailStatus' | 'smsStatus' | 'seen' | 'acknowledged';

/** sent = delivered; failed = has an error; pending = neither yet. */
export type DeliveryStatusFilter = 'sent' | 'pending' | 'failed';

export interface RecipientPageQuery {
  page: number;
  pageSize: number;
  search?: string;
  /** 1–120: only recipients without an hour entry on the announcement's projects in this many months. */
  inactiveMonths?: number | null;
  sort?: RecipientSortField;
  dir?: 'asc' | 'desc';
  seen?: 'seen' | 'unseen';
  email?: DeliveryStatusFilter;
  sms?: DeliveryStatusFilter;
  /** Employer names; '' matches recipients with no employer. */
  employers?: string[];
  ackState?: 'pending' | 'done';
}

export interface CreateAnnouncementDto {
  title: string;
  body: string;
  audience: AnnouncementAudience;
  channels: AnnouncementChannel;
  projectIds?: number[]; // empty = system-wide (all sites)
  excludedUserIds?: string[]; // user IDs to skip (from preview deselect)
  targetRoleIds?: string[];
  targetGroupIds?: number[];
  includedUserIds?: string[];
  excludeInactive?: boolean; // skip deactivated users (Active == false)
  excludeNeverLoggedIn?: boolean; // skip users who have never signed in (LastLogin == null)
  excludeWorkersWithoutRecentHourEntriesMonths?: number;
  priority?: AnnouncementPriority;
  requiresAcknowledgment?: boolean;
  /** UTC ISO string. Create: implies a draft. Update: null clears the schedule. */
  scheduledPublishAt?: string | null;
  isDraft?: boolean;
}

export interface UpdateAnnouncementDto {
  title: string;
  body: string;
  audience: AnnouncementAudience;
  channels: AnnouncementChannel;
  projectIds?: number[];
  excludedUserIds?: string[];
  /** Omitted = backend keeps the stored value; [] clears it. */
  targetRoleIds?: string[];
  targetGroupIds?: number[];
  includedUserIds?: string[];
  excludeInactive?: boolean;
  excludeNeverLoggedIn?: boolean;
  excludeWorkersWithoutRecentHourEntriesMonths?: number;
  priority?: AnnouncementPriority;
  requiresAcknowledgment?: boolean;
  /** UTC ISO string. Create: implies a draft. Update: null clears the schedule. */
  scheduledPublishAt?: string | null;
}

export interface AnnouncementStats {
  emailApplicable: boolean;
  smsApplicable: boolean;
  totalRecipients: number;
  emailSentCount: number;
  emailFailedCount: number;
  smsSentCount: number;
  smsFailedCount: number;
  seenCount: number;
  notSeenCount: number;
  requiresAcknowledgment: boolean;
  acknowledgedCount: number;
  notAcknowledgedCount: number;
  /** Recipients a reminder would reach now (unread, or unacknowledged when required). Absent on API builds without reminders. */
  unreadReminderCount?: number;
  /** ISO 8601 UTC of the latest reminder; null/absent if none. */
  lastReminderAt?: string | null;
  lastReminderRecipientCount?: number | null;
  /** ISO 8601 UTC; null/absent when a reminder may be sent now. */
  nextReminderAllowedAt?: string | null;
}

/** POST /api/announcements/{id}/remind */
export interface AnnouncementReminderResult {
  recipientCount: number;
  emailQueued: boolean;
  smsQueued: boolean;
  sentAt: string;
  nextReminderAllowedAt: string;
}

export interface NextDeliveryInfo {
  nextDeliveryAt: string | null; // ISO 8601 UTC, null until first cycle completes
  intervalSeconds: number;
}

/** PUT /api/announcements/{id}/content — edit a PUBLISHED announcement (drafts use updateDraft). */
export interface EditPublishedAnnouncementDto {
  title: string;
  body: string;
  priority: AnnouncementPriority;
  requiresAcknowledgment: boolean;
  /** Reset everyone's read + acknowledgment so it counts as new again. */
  requestReread: boolean;
  /** Re-send email/SMS (only when the announcement's channels include them). */
  renotify: boolean;
}

/** GET /api/announcements/{id}/revisions — a replaced version, newest first. */
export interface AnnouncementRevision {
  version: number;
  title: string;
  body: string;
  priority: AnnouncementPriority;
  requiresAcknowledgment: boolean;
  /** When this version became visible (UTC). */
  validFrom: string;
  /** When it was replaced (UTC). */
  validUntil: string;
  replacedByName: string | null;
  /** Flags of the edit that replaced this version. */
  rereadRequested: boolean;
  renotified: boolean;
}

/** GET /api/announcements/my/attention — what needs the caller's attention. */
export interface AnnouncementAttention {
  /** Unread, or read but awaiting a required acknowledgment — any priority. Drives the bell. */
  attentionCount: number;
  /** Normal-priority items needing attention. Drives the arrival banner. */
  bannerCount: number;
  /** High-priority items needing attention, oldest first. Drive the blocking dialog. */
  blocking: AnnouncementAttentionItem[];
}

export interface AnnouncementAttentionItem {
  id: number;
  title: string;
  requiresAcknowledgment: boolean;
}
